/**
 * THE READ ROUTES, typed end to end.
 *
 * Every return type here is a `@kinetiq/wire` symbol, and `packages/wire/tests/conformance.test.ts` pins each
 * one to the declaration in `StoreFacade` that the server actually serializes. So a field added, removed or
 * re-typed in the store is a COMPILE ERROR in this app — which is the whole point, because the alternative is
 * a runtime `undefined` rendering as an empty cell that nobody notices for a week.
 *
 * THERE IS NO `?commitment` PARAMETER, on any of these, deliberately. Users are ALWAYS served the processed
 * tip. `finalized` survives only as `asOf.finalizedSlot`, which is what the tape's settled/pending dot reads.
 *
 * EVERY RESPONSE CARRIES `asOf`. That is not decoration either — it is the position the WS stitch cuts at,
 * and `asOf.slot` is the only ordering stamp any merge uses.
 */

import type {
  AsOf,
  CandleMode,
  CandlesResponse,
  GenesisTradersResponse,
  HoldersResponse,
  Interval,
  LaunchSpec,
  LaunchesResponse,
  SearchResponse,
  TokenInfoResponse,
  TopResponse,
  TopSpec,
  TradeMode,
  TradesResponse,
  WalletActivityEntry,
  WalletResponse,
} from "@kinetiq/wire";
import { request } from "./http.ts";

/**
 * `GET /v1/top`.
 *
 * The argument is `TopSpec` — THE SAME TYPE the WS subscribe carries — so a board cannot seed with one spec
 * and subscribe with another. That was a real class of bug: a spec the socket accepts and REST refuses is a
 * board no client can seed, and it only shows up on the sort nobody clicks.
 */
/**
 * `GET /v1/top` — THE SEED, AND IT MUST CARRY EVERY FIELD OF THE SPEC THE SOCKET SUBSCRIBED WITH.
 *
 * ⚠️ THE FILTERS ARE PART OF THE BOARD'S IDENTITY, NOT A RENDERING OPTION. They are applied at ADMISSION, so
 * a filtered board contains rows an unfiltered one never had and vice versa — they are two different
 * rankings. Seed from one and stream the other and the client is applying deltas to a population they were
 * never computed against.
 *
 * MEASURED when this omitted them: the seed fetched the UNFILTERED 250, the socket subscribed to the
 * FILTERED board, and its first frame was a 19-row DELTA (`mints: []`) rather than a snapshot — correct for
 * a client that had seeded the same board, wrong for one that had not. The list went 117 rows -> 62 -> 65 in
 * the first eight seconds and settled at a quarter of the board, which reads exactly like "the API is not
 * being fetched at all".
 *
 * So this takes the WHOLE spec and serializes all of it. A field added to `TopSpec` and not added here is
 * the same defect again, which is why the params are spread from the spec rather than listed a second time.
 */
export function fetchTop(spec: TopSpec, signal?: AbortSignal): Promise<TopResponse> {
  return request<TopResponse>({
    base: "market",
    path: "/top",
    params: {
      sort: spec.sort,
      window: spec.window,
      limit: spec.limit,
      // ALL THREE STATES, EXPLICITLY — `any` is the wire's "no constraint". Omitting the parameter would
      // leave the ROUTE's default in force, and the route's default is deliberately different from the
      // client's (there, absent means no constraint; here, the board is filtered by default).
      verified: spec.filter.verified === null ? "any" : String(spec.filter.verified),
      flagged: spec.filter.flagged === null ? "any" : String(spec.filter.flagged),
    },
    ...(signal ? { signal } : {}),
  });
}

/**
 * `GET /v1/launches` — THE LAUNCH FEED'S SEED, and the SECOND route whose argument is the WS subscribe spec.
 *
 * The argument is `LaunchSpec`, exactly as `fetchTop`'s is `TopSpec`, and for the same reason: a spec the
 * socket accepts and REST refuses is a board no client can seed. The params are spread from the spec rather
 * than listed a second time, so a field added to `LaunchSpec` and not added here is a compile error and not a
 * seed that silently disagrees with the subscription.
 *
 * THERE IS NO `sort`, ON PURPOSE AND NOT BY OMISSION. Chronological is the only ordering that needs no data a
 * newborn row might not have yet; a server-side `sort` would mint a topic per ordering, and ranking tokens
 * minutes old ranks whoever is faking the metric hardest. Any ranked view of this page is a client-side sort.
 *
 * IT IS IN THE GENERAL RATE-LIMIT BUCKET AND COSTS NOTHING: the route issues zero database queries and zero
 * vendor calls at any request rate, because every field it serves is on the create event we folded or on our
 * own fold. That is a property worth stating here, because this surface's population IS the ~2,500-mints-an-
 * hour firehose and it is the one most exposed to a per-token vendor lookup being added "just for the board".
 */
export function fetchLaunches(spec: LaunchSpec, signal?: AbortSignal): Promise<LaunchesResponse> {
  return request<LaunchesResponse>({
    base: "market",
    path: "/launches",
    params: { window: spec.window, limit: spec.limit },
    ...(signal ? { signal } : {}),
  });
}

/** `GET /v1/token/:mint/info`. A mint we have never seen is a 200 with `identityPending: true`, never a 404 —
 *  it is exactly the mint that must enqueue enrichment, so the caller renders a pending state, not an error. */
export function fetchTokenInfo(mint: string, signal?: AbortSignal): Promise<TokenInfoResponse> {
  return request<TokenInfoResponse>({
    base: "market",
    path: `/token/${encodeURIComponent(mint)}/info`,
    ...(signal ? { signal } : {}),
  });
}

export interface CandlesQuery {
  interval: Interval;
  /** Chain seconds. OMITTED means "the server's own clamp supplies the window" — which is different from
   *  passing a computed `now`, because a wall-clock `now` is 3–12 s AHEAD of chain time and asks for a window
   *  whose newest seconds cannot exist yet. */
  from?: number;
  to?: number;
  limit?: number;
  /**
   * WHICH BUCKETS THE PAGE MATERIALISES — and therefore what an ABSENT one means.
   *
   * `traded` (the server's default) carries a bar only where the token traded, so an absent bucket inside the
   * echoed window means nothing traded there. `clock` carries every bucket, silent ones flat at the last
   * close, ending at the confirmed frontier and never at a wall clock.
   */
  mode?: CandleMode;
}

/**
 * `GET /v1/token/:mint/candles`.
 *
 * ⚠️ THE STITCH READS `from`/`to` OFF THE RESPONSE, NEVER OFF THIS QUERY. The server clamps, and the
 * RangeStamp must be installed over the window it ACTUALLY answered. Stamping the requested window installs a
 * cut over seconds the response never covered, and every live bar inside that phantom range is then discarded
 * as stale — a chart that goes quiet for exactly as long as the over-claim.
 */
export function fetchCandles(
  mint: string,
  q: CandlesQuery,
  signal?: AbortSignal,
): Promise<CandlesResponse> {
  return request<CandlesResponse>({
    base: "market",
    path: `/token/${encodeURIComponent(mint)}/candles`,
    params: {
      interval: q.interval,
      from: q.from,
      to: q.to,
      limit: q.limit,
      // OMITTED means the server's default, which is `traded`. It is sent as the word the route validates
      // against — the vocabulary is `@kinetiq/wire`'s `CANDLE_MODES` and the route's accepted set is the
      // store's own array, so a mode this client can name is a mode the server accepts.
      mode: q.mode,
      // ⚠️ ALWAYS `0`, AND IT IS NOT A PREFERENCE. This page is merged into the SAME `BarsState` the socket
      // patches, and the socket never chains. Taking the route's chained default here would give one bucket
      // two different opens depending on which transport delivered it last, and would ALSO destroy the
      // measured open this client needs: `connectOpens` refuses to connect across an unread or `barren`
      // span, and it can only refuse if the bar still carries its own open. Chaining is done HERE, over the
      // whole held window, so a correction re-derives every open below it in the same apply.
      chain: 0,
    },
    ...(signal ? { signal } : {}),
  });
}

export interface TradesQuery {
  mode: TradeMode;
  limit?: number;
  /** The opaque cursor of the last row of the previous page. NEVER partially honoured by the server: a
   *  malformed one is a 400, not a coarser filter. */
  before?: string;
  trader?: string;
}

/**
 * `GET /v1/token/:mint/trades`.
 *
 * `leg` and `tx` are TWO TOPICS whose rows must never be merged into one list: the predicates are not
 * disjoint, so a single-hop trade appears in both under two different identities. One mode per view.
 */
export function fetchTrades(
  mint: string,
  q: TradesQuery,
  signal?: AbortSignal,
): Promise<TradesResponse> {
  return request<TradesResponse>({
    base: "market",
    path: `/token/${encodeURIComponent(mint)}/trades`,
    params: { mode: q.mode, limit: q.limit, before: q.before, trader: q.trader },
    ...(signal ? { signal } : {}),
  });
}

/**
 * `GET /v1/token/:mint/holders` — THE RANKED HOLDER LIST.
 *
 * ⚠️ NO PARAMETERS, AND IT IS FETCHED ONCE PER MOUNT — NOT POLLED, and not stitched either.
 *
 * There is no `holders` WS channel and this client must not invent a poll in its place.
 *
 * ⚠️ THE REASON GIVEN HERE WAS "NOTHING IN THE SERVER'S FOLD MOVES IT — a re-read a second later returns
 * byte-identical rows", AND THAT IS NO LONGER TRUE OF THE VALUES. The server now serves each holder's balance
 * base+overlay: the anchor is the base and `state.balance` — folded for every balance change at the processed
 * tip — is the overlay, so a re-read DOES move as those owners trade. What is frozen between reads is the
 * RANKING's membership, which changes only when the mint is re-anchored.
 *
 * The conclusion survives the correction, for a different reason. A cadence here would imply, on screen, that
 * the top-K itself is live when only its values are — and since 2026-08-28 a mount is also what SCHEDULES a
 * re-anchor when the ranking is stale (via the candle read's `viewedMints` report), so polling would drive
 * paid vendor work rather than merely re-reading it.
 *
 * The staleness is not hidden — it is `asOf.slot - asOfSlot`, in our own chain positions, and the panel
 * renders it.
 */
export function fetchHolders(mint: string, signal?: AbortSignal): Promise<HoldersResponse> {
  return request<HoldersResponse>({
    base: "market",
    path: `/token/${encodeURIComponent(mint)}/holders`,
    ...(signal ? { signal } : {}),
  });
}

/**
 * `GET /v1/token/:mint/traders` — A GENESIS TOKEN'S BUYER LIST, first buyer first.
 *
 * ⚠️ IT TAKES NO PARAMETERS HERE, AND THAT IS THE SEED/PUSH CONTRACT rather than a shortage of options. The
 * route accepts `limit` and `status`; the `traders` channel publishes
 * `genesisTraders(mint, { limit: TRADERS_LIMIT.fallback })` — "the exact body this route serializes with no
 * query string", at the route's own default of 100. A seed that passed either parameter would install a page
 * the channel's very next frame REPLACES with a different population: a `status`-filtered seed under an
 * unfiltered stream, or 250 rows that become 100 on the first flush. It is the same failure `fetchTop`'s
 * comment records for the board's filters, one channel over — so there is one spelling of the page and both
 * halves of the stitch use it.
 *
 * THE PAGE IS A REPLACEMENT AND NEVER A DELTA, which is why `stream/views.ts` folds it as SCALAR state: every
 * number on a row is a cumulative a fork can LOWER, and `buyerIdx` is derived at read over the mint's whole
 * buyer list, so a repair that surfaces an earlier first buyer renumbers everyone below her.
 */
export function fetchTraders(mint: string, signal?: AbortSignal): Promise<GenesisTradersResponse> {
  return request<GenesisTradersResponse>({
    base: "market",
    path: `/token/${encodeURIComponent(mint)}/traders`,
    ...(signal ? { signal } : {}),
  });
}

/**
 * `GET /v1/search?q=…` — THE ONE ROUTE WITH NO SEED AND NO CHANNEL.
 *
 * It is not stitched, and that is a property of the QUESTION rather than a shortcut: a typed string is a
 * one-shot question, and a subscription keyed by one would mint a topic per keystroke. So this is the only
 * read on the surface a caller may issue on its own cadence — and the cadence is therefore the caller's
 * responsibility, which is why `features/search/` owns the one debounce in the app.
 *
 * `q` IS SENT ALREADY NORMALISED and never shorter than the server's minimum. A shorter one is a `400`, not
 * an empty list: the server refuses to LOOK rather than claiming nothing matched, and a client that sent it
 * anyway would render that refusal as "no results" — the one reading that makes a coverage gap invisible.
 *
 * IT IS IN THE GENERAL RATE-LIMIT BUCKET, not the expensive one: it awaits nothing, issues no query and
 * makes no vendor call, and its repeat cost is bounded by a 5-minute match cache rather than by the request
 * rate. That is what makes a debounce a courtesy rather than the only thing standing between a keystroke and
 * a bill — but the debounce stays, because a request per keystroke spends a cache miss's enrichment offer and
 * a slot of the inbound bucket for a query the user has not finished typing.
 */
export function fetchSearch(
  query: string,
  limit: number | undefined,
  signal?: AbortSignal,
): Promise<SearchResponse> {
  return request<SearchResponse>({
    base: "market",
    path: "/search",
    params: { q: query, limit },
    ...(signal ? { signal } : {}),
  });
}

/**
 * `GET /v1/wallet/:owner` — THE SEED FOR THE `wallet` CHANNEL, exactly like the other screens.
 *
 * This header read *"the one screen-seeding read with no WS channel, which is why it is the one screen that
 * re-reads"* until the channel landed. It is a seed now: `stream/views.ts`'s `walletView` subscribes and this
 * read fills it, and the read is ALSO what admits the address to the store's tracked set — a subscribe with
 * no seed behind it acks and then stays silent forever. (`/search` still has no channel and needs none: it
 * answers a question asked once, and there is nothing for a stream to keep current.)
 *
 * ⚠️ IT IS A PUBLIC MARKET QUESTION, NOT THE SIGNED-IN WALLET. It carries no session and no bearer, exactly
 * like the other four — `auth` is deliberately not set. The signed-in balance screen is the trader surface's
 * `GET /me/balances`, which is a different route on a different server with a different stamp
 * (`observedAt: {slot}`, spelled differently so it cannot be merged with a market `AsOf`).
 *
 * SO NOTHING HERE POLLS. The route is in the EXPENSIVE bucket (2 rps sustained, 20 burst, plus a global
 * 16-wide semaphore), which is affordable for a seed and was never affordable as a cadence.
 */
export function fetchWallet(owner: string, signal?: AbortSignal): Promise<WalletResponse> {
  return request<WalletResponse>({
    base: "market",
    path: `/wallet/${encodeURIComponent(owner)}`,
    ...(signal ? { signal } : {}),
  });
}

/**
 * One entry on a wallet's activity tape — RE-EXPORTED FROM `@kinetiq/wire`, never re-declared.
 *
 * It was hand-written here, structurally equal to the store's, and that stopped being survivable when the
 * same type started arriving on TWO paths: `fetchWalletActivity` returns it as a page, and `WalletResponse`
 * now carries it as the live tip. A panel that merges the two must be merging ONE type, or a field added on
 * the server compiles here and renders blank on whichever half kept the old shape.
 */
export type { WalletActivityEntry };

/** A mint's identity, as an activity row needs it. Every field is nullable: enrichment is asynchronous and
 *  partial, so `decimals` can land before the metadata JSON does. */
export interface ActivityToken {
  readonly name: string | null;
  readonly symbol: string | null;
  readonly image: string | null;
  readonly decimals: number | null;
}

export interface WalletActivityResponse {
  readonly asOf: AsOf;
  readonly owner: string;
  /** `null` = no durable reader configured. NOT the same as `[]` — see below. */
  readonly entries: readonly WalletActivityEntry[] | null;
  /**
   * Mint → identity, for every mint on this page we hold one for.
   *
   * AN ABSENT MINT IS NOT AN ERROR: the server has QUEUED it for enrichment (one account read, free tier)
   * and it will be present on a later read. Render a short address meanwhile — never a blank, and never a
   * guessed decimal, because a wrong exponent is a wrong number and looks exactly like a right one.
   */
  readonly tokens: Readonly<Record<string, ActivityToken>>;
  readonly limit: number;
  /** Cursor for the next (older) page, or `null` at the end of the tape. */
  readonly nextBefore: string | null;
}

/**
 * `GET /v1/wallet/:owner/activity` — WHAT THIS WALLET DID, newest first.
 *
 * ⚠️ `entries: null` IS NOT `[]`, and a renderer must not collapse them. `null` means the server has no
 * durable reader configured; `[]` means we hold no activity for this wallet. Showing "no activity" for the
 * first tells somebody hunting a missing transaction that it never happened, which is the worst wrong answer
 * this screen can give.
 *
 * ⚠️ THERE ARE NO USD VALUES AND THERE CANNOT BE. These rows sit below our price floor — `token_price` and
 * `candles_1s` both start above their slots — so the tape carries AMOUNTS and nothing values them. A caller
 * that wants a dollar figure is asking a question the data cannot answer.
 *
 * AMOUNTS ARE RAW BASE UNITS AS STRINGS. A `number` loses the low digits of any u64 above 2^53, which a
 * 9-decimal mint reaches at ~9.007 tokens. Scale with the mint's decimals at render time; never parse to a
 * float first.
 *
 * PAGING IS BY CURSOR, never by offset: the backfill writes older rows while a user pages, so an offset would
 * repeat or skip. Pass the previous response's `nextBefore` verbatim.
 */
export function fetchWalletActivity(
  owner: string,
  opts: { limit?: number; before?: string } = {},
  signal?: AbortSignal,
): Promise<WalletActivityResponse> {
  const q = new URLSearchParams();
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.before !== undefined) q.set("before", opts.before);
  const qs = q.toString();
  return request<WalletActivityResponse>({
    base: "market",
    path: `/wallet/${encodeURIComponent(owner)}/activity${qs === "" ? "" : `?${qs}`}`,
    ...(signal ? { signal } : {}),
  });
}
